import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import {
  ApprovalStatus,
  ApprovalStatusLabel,
  convertApprovalStatus,
} from '../../../../../../../api/enumerations';
import {
  getPairWaitlist,
  offerWaitlistSlot,
} from '../../../../../../../api/theHive/noticeCoverage';
import {
  PairWaitlistData,
  WaitlistQueueEntry,
} from '../../../../../../../api/theHive/noticeCoverage/types';
import { IconCloseMS } from '../../../../../../../constants/icons';
import {
  formatCnpjCpf,
  formatSaoPauloDateTime,
} from '../../../../../../../helpers';
import useErrorMessage from '../../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../../hooks/useSnackbar';
import { TableCard } from '../styles';

// reuses the centralized converter so wording never drifts from other tables
const APPROVAL_STATUS_BY_LABEL: Record<string, ApprovalStatus> = {
  [ApprovalStatusLabel.PENDING]: ApprovalStatus.PENDING,
  [ApprovalStatusLabel.APPROVED]: ApprovalStatus.APPROVED,
  [ApprovalStatusLabel.REJECTED]: ApprovalStatus.REJECTED,
  [ApprovalStatusLabel.DISCREDITED]: ApprovalStatus.DISCREDITED,
  [ApprovalStatusLabel.AWAITING_FORMALIZATION]:
    ApprovalStatus.AWAITING_FORMALIZATION,
  [ApprovalStatusLabel.FORMALIZATION_SUBMITTED]:
    ApprovalStatus.FORMALIZATION_SUBMITTED,
};

const approvalStatusLabel = (raw: string): string => {
  const status = APPROVAL_STATUS_BY_LABEL[raw];
  return status ? convertApprovalStatus(status) : raw;
};

interface WaitlistPanelProps {
  noticeId: number;
  poleId: number;
  poleName: string;
  criteriaGroupId: number;
  criteriaGroupName: string;
  onClose: () => void;
  // an accepted offer holds the slot, so the limits table must refresh too
  onOffered: () => Promise<void>;
}

// per-pair waitlist (PROT-2955): freed slots are OFFERED to the first in
// line (the offer holds the slot until accepted/rejected/expired); the
// button is the manual-offer exception to the queue order
export function WaitlistPanel({
  noticeId,
  poleId,
  poleName,
  criteriaGroupId,
  criteriaGroupName,
  onClose,
  onOffered,
}: WaitlistPanelProps): JSX.Element {
  const [data, setData] = useState<PairWaitlistData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [entryToOffer, setEntryToOffer] = useState<WaitlistQueueEntry | null>(
    null
  );
  const [isOffering, setIsOffering] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const fetchWaitlist = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await getPairWaitlist(noticeId, poleId, criteriaGroupId);
      if (response instanceof Error) throw response;
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      setData(response.data ?? null);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId, poleId, criteriaGroupId]);

  useEffect(() => {
    fetchWaitlist();
  }, [fetchWaitlist]);

  const handleConfirmOffer = async (): Promise<void> => {
    if (!entryToOffer) return;
    setIsOffering(true);
    try {
      const response = await offerWaitlistSlot(noticeId, {
        notice_company_id: entryToOffer.notice_company_id,
        pole_id: poleId,
        criteria_group_id: criteriaGroupId,
      });
      if (response instanceof Error) throw response;
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      handleSnackbar('Vaga ofertada. Aguardando resposta da empresa.', false);
      setEntryToOffer(null);
      // the panel stays mounted during the refresh, so it needs its own fetch
      await Promise.all([fetchWaitlist(), onOffered()]);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setIsOffering(false);
    }
  };

  const canOffer = (entry: WaitlistQueueEntry): boolean =>
    entry.state === 'queued' && (data?.available_slots ?? 0) > 0;

  const capacitySummary = data
    ? [
        { label: 'Credenciados ativos', value: data.active_accreditations },
        { label: 'Ofertas pendentes', value: data.pending_offers },
        { label: 'Vagas livres', value: data.available_slots },
        { label: 'Na fila', value: data.waitlist_count },
      ]
    : [];

  return (
    <TableCard sx={{ p: '20px' }}>
      <Box
        alignItems="flex-start"
        display="flex"
        justifyContent="space-between"
      >
        <Box>
          <Typography fontSize={18} fontWeight={600}>
            Fila de espera — {poleName} · {criteriaGroupName}
          </Typography>
          <Typography color="text.secondary" fontSize={14} mt={0.5}>
            Vagas liberadas são ofertadas automaticamente ao primeiro da fila; a
            oferta retém a vaga até a resposta da empresa ou o fim do prazo.
          </Typography>
        </Box>
        <IconButton aria-label="fechar fila de espera" onClick={onClose}>
          {IconCloseMS}
        </IconButton>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        data && (
          <>
            <Box display="flex" flexWrap="wrap" gap={1} my={2}>
              {capacitySummary.map((item) => (
                <Chip
                  key={item.label}
                  label={`${item.label}: ${item.value}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>

            {data.queue.length === 0 ? (
              <Typography color="text.secondary" py={2}>
                Nenhuma empresa na fila de espera deste par.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Posição</TableCell>
                      <TableCell>Empresa</TableCell>
                      <TableCell>CNPJ</TableCell>
                      <TableCell>Aguardando desde</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Situação na fila</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.queue.map((entry) => (
                      <TableRow key={entry.notice_company_id}>
                        <TableCell>
                          <Typography fontWeight={600}>
                            {entry.position}º
                          </Typography>
                        </TableCell>
                        <TableCell>{entry.company_name}</TableCell>
                        <TableCell>{formatCnpjCpf(entry.cnpj)}</TableCell>
                        <TableCell>
                          {formatSaoPauloDateTime(entry.waitlisted_at)}
                        </TableCell>
                        <TableCell>
                          {approvalStatusLabel(entry.approval_status)}
                        </TableCell>
                        <TableCell>
                          {entry.state === 'offered' ? (
                            <Chip
                              color="warning"
                              label={`oferta pendente até ${formatSaoPauloDateTime(
                                entry.offer_expires_at
                              )}`}
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="na fila"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            disabled={!canOffer(entry)}
                            onClick={() => setEntryToOffer(entry)}
                            size="small"
                            sx={{ textTransform: 'none' }}
                            variant="outlined"
                          >
                            Ofertar vaga
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )
      )}

      <Dialog onClose={() => setEntryToOffer(null)} open={!!entryToOffer}>
        <DialogTitle>Ofertar vaga</DialogTitle>
        <DialogContent>
          <Typography>
            Ofertar a vaga do par {poleName} · {criteriaGroupName} para{' '}
            <strong>{entryToOffer?.company_name}</strong>
            {entryToOffer && entryToOffer.position > 1
              ? ` (posição ${entryToOffer.position}º — exceção à ordem da fila)`
              : ''}
            ? A vaga fica retida até a empresa aceitar, rejeitar ou o prazo da
            oferta expirar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isOffering}
            onClick={() => setEntryToOffer(null)}
            sx={{ textTransform: 'none' }}
            variant="text"
          >
            Cancelar
          </Button>
          <Button
            disabled={isOffering}
            onClick={handleConfirmOffer}
            sx={{ textTransform: 'none' }}
            variant="contained"
          >
            {isOffering ? 'Ofertando...' : 'Confirmar oferta'}
          </Button>
        </DialogActions>
      </Dialog>
    </TableCard>
  );
}
